import React, { createContext, useContext, useEffect, useReducer, useRef, useState, useCallback } from 'react'
import { User, Session, AuthChangeEvent } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { getUserProfile } from './api/api'
import { env } from './env'
import { classifyPortalMembership, type PortalMembership } from './portal-membership'
import { useInactivityTimeout } from '../hooks/use-inactivity-timeout'
import { toast } from 'sonner'
import { InactivityWarningToast } from '../components/inactivity-warning-toast'

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'mfa_required'

interface AuthState {
  status: AuthStatus
  user: User | null
  session: Session | null
  isBanned: boolean
  bannedUntil: string | null
}

type AuthAction = { type: 'SET_AUTH_STATE'; payload: Partial<AuthState> } | { type: 'LOGOUT' }

const initialState: AuthState = {
  status: 'loading',
  user: null,
  session: null,
  isBanned: false,
  bannedUntil: null,
}

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'SET_AUTH_STATE':
      return { ...state, ...action.payload }
    case 'LOGOUT':
      return { ...initialState, status: 'unauthenticated' }
    default:
      return state
  }
}

export interface AuthContextType extends AuthState {
  /** Set when this portal rejected the user. Null normally. */
  wrongPortal: PortalMembership | null
  /** True while the rejection is redirecting the user to their own portal (destination configured). */
  wrongPortalRedirecting: boolean
  login: (email: string, password: string) => Promise<void>
  signup: (email: string, password: string) => Promise<{ session: Session | null; user: User | null }>
  logout: (doRedirect?: boolean) => Promise<void>
  refreshSession: () => Promise<void>
  getToken: () => string | null
  verifyMFA: (code: string) => Promise<void>
  // Configuration
  inactivityTimeoutMs: number
  warningTimeMs: number
}

interface AuthProviderProps {
  children: React.ReactNode
  inactivityTimeoutMs?: number
  warningTimeMs?: number
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({
  children,
  inactivityTimeoutMs = 30 * 60 * 1000,
  warningTimeMs = 60 * 1000,
}: AuthProviderProps) {
  const [state, dispatch] = useReducer(authReducer, initialState)
  const warningToastIdRef = useRef<string | number | null>(null)
  // Outside the reducer: LOGOUT resets it to initial state, wiping why the session ended.
  const [wrongPortal, setWrongPortal] = useState<PortalMembership | null>(null)
  const [wrongPortalRedirecting, setWrongPortalRedirecting] = useState(false)
  const rejectingRef = useRef(false)

  const logout = useCallback(async (doRedirect = false) => {
    try {
      console.log('[AuthContext] Starting logout process')
      if (warningToastIdRef.current) {
        toast.dismiss(warningToastIdRef.current)
        warningToastIdRef.current = null
      }

      await supabase.auth.signOut()

      dispatch({ type: 'LOGOUT' })

      const clearStorage = (storage: Storage) => {
        const keysToRemove = []
        for (let i = 0; i < storage.length; i++) {
          const key = storage.key(i)
          if (key && (key.startsWith('incept5-auth') || key.startsWith('supabase'))) {
            keysToRemove.push(key)
          }
        }
        keysToRemove.forEach((key) => storage.removeItem(key))
      }

      clearStorage(localStorage)
      clearStorage(sessionStorage)

      if (doRedirect) {
        window.location.hash = '#/auth/login'
      }
    } catch (error) {
      console.error('Exception during logout:', error)
      dispatch({ type: 'LOGOUT' })
    }
  }, [])

  /**
   * FF-3636. Sign-out precedes the redirect and is never conditional on having a destination: a
   * persisted session auto-restores and bounces again. 'unknown' has no home portal.
   */
  const rejectWrongPortal = useCallback(async (membership: PortalMembership) => {
    if (rejectingRef.current) return
    rejectingRef.current = true

    const destination =
      membership === 'backoffice'
        ? env.VITE_ADMIN_DASHBOARD_URL
        : membership === 'partner'
        ? env.VITE_PARTNER_DASHBOARD_URL
        : undefined

    console.log('[AuthContext] Wrong portal for this user, signing out', { membership, destination })
    await supabase.auth.signOut()
    dispatch({ type: 'LOGOUT' })
    setWrongPortal(membership)
    setWrongPortalRedirecting(!!destination)

    if (destination) {
      window.location.replace(destination)
    }
  }, [])

  const getUserSession = useCallback(
    async (session: Session | null) => {
      console.log('[AuthContext] Loading user session', session)
      if (!session) {
        console.log('[AuthContext] No session found, logging out')
        dispatch({ type: 'LOGOUT' })
        return
      }

      // Fresh attempt: clear any prior rejection, or the next sign-in short-circuits before signOut.
      rejectingRef.current = false
      setWrongPortal(null)
      setWrongPortalRedirecting(false)

      try {
        const { data: mfaData, error: mfaError } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
        if (mfaError) throw mfaError
        const currentLevel = mfaData?.currentLevel ?? 'aal1'
        if (currentLevel !== 'aal2') {
          dispatch({ type: 'SET_AUTH_STATE', payload: { status: 'mfa_required', user: session.user, session } })
          return
        }
        console.log('[AuthContext] MFA level:', currentLevel)

        const userProfile = await getUserProfile()

        // FF-3636: gate here, not in ProtectedRoute, so it runs on every auth state change.
        const membership = classifyPortalMembership(userProfile)
        if (membership !== 'merchant') {
          await rejectWrongPortal(membership)
          return
        }

        const isBanned = !!userProfile.bannedUntil && new Date(userProfile.bannedUntil) > new Date()

        if (isBanned) {
          dispatch({
            type: 'SET_AUTH_STATE',
            payload: {
              status: 'authenticated',
              user: session.user,
              session,
              isBanned: true,
              bannedUntil: userProfile.bannedUntil,
            },
          })
        } else {
          dispatch({
            type: 'SET_AUTH_STATE',
            payload: {
              status: 'authenticated',
              user: session.user,
              session,
              isBanned: false,
              bannedUntil: null,
            },
          })
        }
      } catch (error) {
        console.error('[AuthContext] Error loading user session:', error)
        await logout()
      }
    },
    [logout, rejectWrongPortal],
  )

  const handleInactivityTimeout = useCallback(async () => {
    console.log('[AuthContext] Session expired due to inactivity, logging out')
    sessionStorage.setItem('session_expired_reason', 'inactivity')
    await logout(true)
  }, [logout])

  const handleInactivityWarning = useCallback((secondsRemaining: number) => {
    if (warningToastIdRef.current) return
    const handleStayLoggedIn = () => {
      if (warningToastIdRef.current) {
        toast.dismiss(warningToastIdRef.current)
        warningToastIdRef.current = null
      }
    }
    warningToastIdRef.current = toast(
      <InactivityWarningToast initialSeconds={secondsRemaining} onStayLoggedIn={handleStayLoggedIn} />,
      { duration: Infinity, className: 'warning-toast' },
    )
  }, [])

  const handleActivity = useCallback(() => {
    if (warningToastIdRef.current) {
      toast.dismiss(warningToastIdRef.current)
      warningToastIdRef.current = null
    }
  }, [])

  useInactivityTimeout({
    timeout: inactivityTimeoutMs,
    onTimeout: handleInactivityTimeout,
    onWarning: handleInactivityWarning,
    warningTime: warningTimeMs,
    isActive: state.status === 'authenticated',
  })

  useEffect(() => {
    if (state.status !== 'authenticated') return

    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click', 'keydown']
    events.forEach((event) => document.addEventListener(event, handleActivity, { passive: true }))
    return () => {
      events.forEach((event) => document.removeEventListener(event, handleActivity))
    }
  }, [state.status, handleActivity])

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event: AuthChangeEvent, session: Session | null) => {
      console.log('[AuthContext] Auth state change event:', event)
      const currentUser = session?.user ?? null

      if (currentUser) {
        getUserSession(session)
      } else {
        dispatch({ type: 'LOGOUT' })
      }
    })

    return () => subscription.unsubscribe()
  }, [getUserSession])

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) throw error
  }

  const verifyMFA = async (code: string) => {
    const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors()
    if (factorsError) throw factorsError
    const verifiedTotpFactor = factors.totp.find((factor) => factor.status === 'verified')
    if (!verifiedTotpFactor) throw new Error('No verified TOTP factor found.')

    const { data: challenge, error: challengeError } = await supabase.auth.mfa.challenge({
      factorId: verifiedTotpFactor.id,
    })
    if (challengeError) throw challengeError

    const { data, error: verifyError } = await supabase.auth.mfa.verify({
      factorId: verifiedTotpFactor.id,
      challengeId: challenge.id,
      code: code,
    })
    if (verifyError) throw verifyError

    if (data) {
      await supabase.auth.setSession(data)
    }
  }

  const signup = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signUp({ email, password })
    if (error) throw error
    return { session: data.session, user: data.user }
  }

  const refreshSession = async () => {
    const {
      data: { session },
      error,
    } = await supabase.auth.refreshSession()
    if (error) throw error
    await getUserSession(session)
  }

  const getToken = (): string | null => {
    return state.session?.access_token ?? null
  }

  return (
    <AuthContext.Provider
      value={{
        ...state,
        wrongPortal,
        wrongPortalRedirecting,
        login,
        signup,
        logout,
        refreshSession,
        getToken,
        verifyMFA,
        inactivityTimeoutMs,
        warningTimeMs,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
